import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/Sidebar";
import CreateLineModal from "./CreateLineModal";
import { App } from "antd";
import theme from "../../themes";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  createGoogleAuthInfoThunk,
  getGoogleAuthInfoThunk,
  getLinesThunk,
} from "../../store/actions/line.action";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ROLES } from "../../models/user.model";

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI || "";
  const scope = encodeURIComponent(
    "https://www.googleapis.com/auth/calendar " +
      "https://www.googleapis.com/auth/calendar.events " +
      "https://www.googleapis.com/auth/userinfo.profile " +
      "https://www.googleapis.com/auth/userinfo.email"
  );
  const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(
    redirectUri
  )}&response_type=code&scope=${scope}&access_type=offline&include_granted_scopes=true&prompt=consent`;

  const [isCreatePhoneOpen, setIsCreatePhoneOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user } = useAppSelector((state) => state.auth);

  const { googleAuthInfo, lines } = useAppSelector((state) => state.line);
  const toast = ToastMessage(message);

  const fetchGoogleAuthInfo = async () => {
    try {
      await dispatch(getGoogleAuthInfoThunk()).unwrap();
    } catch (error) {
      console.error("Error fetching google auth info:", error);
    }
  };

  const fetchLines = async () => {
    try {
      await dispatch(
        getLinesThunk({ limit: 999, page: 1, line_number: "" })
      ).unwrap();
    } catch (error) {
      console.log("error", error);
    }
  };

  const openCreatePhoneModal = () => {
    setIsCreatePhoneOpen(true);
  };

  // Function to extract authorization code from URL
  const extractAuthCode = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("code");
  };

  // Function to send auth code to backend
  const sendAuthCodeToBackend = async (authCode: string) => {
    try {
      await dispatch(createGoogleAuthInfoThunk(authCode)).unwrap();
      toast.success(
        t("settings.googleCalendarConnectedSuccessfully"),
        5,
        <CheckCircleIcon />
      );
      fetchGoogleAuthInfo();
    } catch (error) {
      console.error("Error sending auth code to backend:", error);
      toast.error(t("settings.failedToConnectGoogleCalendar"), 5);
    }
  };

  // Check for auth code when component mounts
  useEffect(() => {
    fetchGoogleAuthInfo();
    fetchLines();
    const authCode = extractAuthCode();
    if (authCode) {
      // Clean the URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);
      // Then send to backend
      sendAuthCodeToBackend(authCode);
    }
  }, []);

  return (
    <FlexRow>
      <MainContent>
        <HeaderRow>
          <Title>{t("settings.title")}</Title>
        </HeaderRow>

        <WhiteBox>
          <SettingsList>
            <SettingsRow>
              <Label>{t("settings.phoneNumber")}</Label>
              {lines.count > 0 ? (
                <ActionButtonLink
                  onClick={() => navigate("/settings/manage-phone-number")}
                >
                  {/* <PhoneNumberCount>{lines.count}</PhoneNumberCount> */}
                  <PhoneNumberText>
                    {" "}
                    {t("settings.managePhoneNumbers")}
                  </PhoneNumberText>
                </ActionButtonLink>
              ) : (
                <ActionButtonLink onClick={openCreatePhoneModal}>
                  {t("settings.createPhoneNumber")}
                </ActionButtonLink>
              )}
            </SettingsRow>
            {user?.role === ROLES.ADMIN && (
              <SettingsRow>
                <Label>{t("settings.calendar")} (beta)</Label>
                {googleAuthInfo?.data ? (
                  <ConnectedStatusContainer>
                    <ConnectedStatus>
                      {t("settings.connected")}
                      <br />
                      <EmailText>
                        ({googleAuthInfo.data.email || t("settings.connected")})
                      </EmailText>
                      <ActionLink href={oauthUrl}>
                        {t("settings.connectToAnotherAccount")}
                      </ActionLink>
                    </ConnectedStatus>
                  </ConnectedStatusContainer>
                ) : (
                  <ActionLink href={oauthUrl}>
                    {t("settings.connectGoogleCalendar")}
                  </ActionLink>
                )}
              </SettingsRow>
            )}
          </SettingsList>
        </WhiteBox>

        <CreateLineModal
          open={isCreatePhoneOpen}
          onCancel={() => setIsCreatePhoneOpen(false)}
          onSuccess={fetchLines}
        />
      </MainContent>
    </FlexRow>
  );
};

const FlexRow = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${theme.colors.grey3};
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 52px 72px;
  gap: 40px;
  flex-direction: column;
  display: flex;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WhiteBox = styled.div`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  background-color: #fff;
  padding: 24px;
`;

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SettingsRow = styled.div`
  display: flex;
`;

const Label = styled.span`
  color: #111827;
  font-size: 16px;
  width: 18%;
`;

const ActionLink = styled.a`
  color: #3b82f6;
  font-size: 16px;
  text-decoration: none;
  cursor: pointer;
`;

const ActionButtonLink = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  color: #3b82f6;
  font-size: 16px;
  cursor: pointer;
`;

const ConnectedStatusContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const ConnectedStatus = styled.span`
  font-weight: 400;
  font-size: 16px;
`;

const EmailText = styled.span`
  font-weight: 400;
  color: ${theme.colors.grey1};
  font-size: 16px;
  margin-right: 8px;
`;

const PhoneNumberCount = styled.span`
  color: #374151;
  font-weight: 400;
  font-size: 16px;
  margin-right: 8px;
`;

const PhoneNumberText = styled.span`
  color: #3b82f6;
  font-weight: 400;
  font-size: 16px;
`;

export default Settings;
